import { useCallback, useEffect, useState } from 'react';
import { BellRing } from 'lucide-react';
import { fetchMyWorkActions, transitionWorkAction } from '@/dashboard/api';
import { AcButton, EmptyState, ErrorState, LoadingState } from '@/dashboard/ui';
import {
  removeFollowupRow,
  type FollowupRow,
  type FollowupTransition,
} from '@/walkin/WalkinFollowupsPanel';

/**
 * AdoraCare - COC follow-up work-queue cockpit
 * (SANDBOX-CASE-SHEET-COC-COCKPIT-V1, 2C-1).
 *
 * The COC vertical of the doctor -> COC recovery loop. The doctor finalizes a
 * price/timing/no_response-declined visit (#182 CaseSheetCockpit); the back-half
 * spawns ONE recovery follow-up on the lead_actions plane
 * (lib/back-half-outcome-followup-hook.ts). This cockpit is where the COC SEES
 * those open follow-ups and ACTS on them (Done / Dismiss) - a LIST-FIRST work
 * queue, not a form. It only DISPLAYS + transitions the rows; it never creates
 * them and touches no backend beyond the two existing work-actions calls.
 *
 * REUSE, not duplication: the read is the SAME `fetchMyWorkActions()` that
 * WalkinFollowupsPanel uses, the transition is the SAME `transitionWorkAction()`,
 * and the post-action row removal is the SAME exported pure `removeFollowupRow`.
 * This cockpit adds only a richer, workflow-first presentation (a count, a
 * neutral age, an honest-ceiling note) on top of that shared read.
 *
 * HARD DATA RULE. Every row renders ONLY what the feed returns: the carved lead
 * name, the server's friendly `reason` label (which for a recovery follow-up is
 * the "Appointment confirmation follow-up" template label - the feed does NOT
 * carry the price/timing/no_response decline token, so this cockpit never
 * fabricates one), and a NEUTRAL age derived from the real `createdAt`. The feed
 * carries NO value/priority column, so there is NO sort-by-value control; it
 * carries NO due_at, so there are NO overdue/today/upcoming buckets. Those
 * market-standard capabilities are stated once as an explicit "coming later"
 * note (COC_COCKPIT_CEILING_NOTE) - never rendered as a control that cannot work.
 *
 * COMPLIANCE. The feed carries no clinical field (clinical_note /
 * findings_diagnosis / chief_complaint), and this cockpit reaches for none - it
 * renders a PHI-safe worklist only.
 *
 * Tested via react-dom/server (no jsdom): the pure helpers + the presentational
 * CocCockpitView are exercised with controlled props (a fixed `nowMs` keeps the
 * age deterministic); the default export wires the real client fns in an effect
 * that SSR does not run (so the static render is the loading state).
 */

// --- Pure helpers (exported for unit tests) --------------------------------

/**
 * NEUTRAL relative age of an open follow-up from its ISO `createdAt`. This is
 * "how long it has been open", NOT an overdue / late / urgency signal - the feed
 * carries no due date, so no such signal is derivable. `nowMs` is injected (never
 * read from a clock here) so the render is deterministic and unit-testable.
 */
export function formatFollowupAge(
  createdAt: string | null,
  nowMs: number,
): string {
  if (createdAt === null) return 'Age unknown';
  const then = Date.parse(createdAt);
  if (Number.isNaN(then)) return 'Age unknown';
  const diffMs = Math.max(0, nowMs - then);
  const days = Math.floor(diffMs / 86_400_000);
  if (days >= 1) return `Opened ${days}d ago`;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours >= 1) return `Opened ${hours}h ago`;
  const mins = Math.floor(diffMs / 60_000);
  if (mins >= 1) return `Opened ${mins}m ago`;
  return 'Opened just now';
}

/** Truthful open-count label ("1 open follow-up" / "N open follow-ups"). */
export function openCountLabel(count: number): string {
  return `${count} open ${count === 1 ? 'follow-up' : 'follow-ups'}`;
}

/**
 * The cockpit's Done / Dismiss behavior as a never-throwing, dependency-injected
 * unit (node-env testable without a click, mirroring the recordOutcome DI idiom):
 * call the injected transition for exactly (id, status); resolve `true` iff it
 * succeeds (the caller then drops the actioned row via the shared
 * removeFollowupRow), and `false` on ANY rejection (the row stays in the queue -
 * a failed transition never fabricates a success). It never throws.
 */
export async function performFollowupTransition(
  transition: (id: number, status: FollowupTransition) => Promise<unknown>,
  id: number,
  status: FollowupTransition,
): Promise<boolean> {
  try {
    await transition(id, status);
    return true;
  } catch {
    return false;
  }
}

/**
 * The single, explicit honest-ceiling note. It states what the v1 queue does
 * (oldest-first) AND what the feed cannot yet support (value ranking, due-date
 * buckets) so the ceiling is truthful rather than a broken-looking control.
 */
export const COC_COCKPIT_CEILING_NOTE =
  'Ordered oldest first, and capped to the oldest open follow-ups - the count is what is loaded here, not necessarily every open follow-up. Value ranking and overdue / today / upcoming views are coming later - the follow-up feed does not carry a value or a due date yet, so this queue does not fake them.';

// --- Presentational state ---------------------------------------------------

export type CockpitState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: FollowupRow[] }
  | { kind: 'error' };

/**
 * The honest read-failure state: an EXPLICIT error, NEVER a fabricated empty
 * `{ kind: 'ready', rows: [] }` (which would misreport a failed read as an empty
 * "queue clear"). Exported + used by the load `.catch` so the invariant is a
 * regression-tested constant, not just an inline literal.
 */
export const COCKPIT_ERROR_STATE: CockpitState = { kind: 'error' };

/**
 * Pure state transition after a Done / Dismiss (node-testable without a click):
 * on success drop the actioned row (reusing the shared removeFollowupRow); on
 * failure OR a non-ready state, return the state UNCHANGED (the row stays - a
 * failed transition never fabricates a success). The default export routes its
 * setState through this so the success->remove / failure->keep glue is covered.
 */
export function applyTransitionResult(
  state: CockpitState,
  id: number,
  ok: boolean,
): CockpitState {
  if (!ok || state.kind !== 'ready') return state;
  return { kind: 'ready', rows: removeFollowupRow(state.rows, id) };
}

/**
 * One follow-up row: carved name + the server `reason` label as a chip + the
 * neutral age + the one-click Done / Dismiss action pair. Exported drop-in; the
 * default export wires `onAction` to the transition call.
 */
export function CocFollowupRow({
  row,
  nowMs,
  busy,
  onAction,
}: {
  row: FollowupRow;
  nowMs: number;
  busy: boolean;
  onAction: (id: number, status: FollowupTransition) => void;
}): JSX.Element {
  return (
    <li className="coc-cockpit__row" data-testid={`coc-followup-${row.id}`}>
      <div className="coc-cockpit__row-main">
        <span className="coc-cockpit__name">
          {row.patientName || 'Unknown patient'}
        </span>
        <span
          className="coc-cockpit__reason"
          data-testid={`coc-followup-reason-${row.id}`}
        >
          {row.reason || 'Follow-up'}
        </span>
        <span className="coc-cockpit__age" data-testid={`coc-followup-age-${row.id}`}>
          {formatFollowupAge(row.createdAt, nowMs)}
        </span>
      </div>
      {/* Standard 5 (rule 6 -- one action primitive): the Done/Dismiss doors
          adopt the shared AcButton (same class, testid, disabled, and label
          markup as before -- one action vocabulary across the surfaces). */}
      <div className="coc-cockpit__row-actions">
        <AcButton
          variant="primary"
          size="sm"
          data-testid={`coc-followup-done-${row.id}`}
          disabled={busy}
          onClick={() => onAction(row.id, 'done')}
        >
          Done
        </AcButton>
        <AcButton
          variant="secondary"
          size="sm"
          data-testid={`coc-followup-dismiss-${row.id}`}
          disabled={busy}
          onClick={() => onAction(row.id, 'dismissed')}
        >
          Dismiss
        </AcButton>
      </div>
    </li>
  );
}

/** Pure, SSR-testable view of the cockpit for a given state. */
export function CocCockpitView({
  state,
  nowMs,
  busyId,
  onAction,
}: {
  state: CockpitState;
  nowMs: number;
  busyId: number | null;
  onAction: (id: number, status: FollowupTransition) => void;
}): JSX.Element {
  return (
    <section
      className="clinic-table coc-cockpit"
      data-testid="coc-cockpit"
      aria-label="Follow-ups to call"
    >
      <div className="coc-cockpit__head">
        <h2 className="clinic-table__title">Follow-ups to call</h2>
        {state.kind === 'ready' ? (
          <span className="coc-cockpit__count" data-testid="coc-cockpit-count">
            {openCountLabel(state.rows.length)}
          </span>
        ) : null}
      </div>
      <p className="clinic-workbench__hint" data-testid="coc-cockpit-subtitle">
        Recovery follow-ups assigned to you, oldest first. Call the patient, then
        mark the follow-up Done - or Dismiss it if it no longer needs a call.
      </p>

      {state.kind === 'loading' ? (
        // V7-P4 adoption: the shared LoadingState (03 P11), copy unchanged.
        <LoadingState label="Loading your follow-ups..." />
      ) : state.kind === 'error' ? (
        // V7-P4 adoption: the shared ErrorState (03 P11 S4), copy unchanged.
        <ErrorState
          title="Your follow-ups are unavailable right now."
          testId="coc-cockpit-error"
        />
      ) : state.rows.length === 0 ? (
        // V7-P4 adoption: the shared EmptyState (03 P11), copy + icon kept.
        <EmptyState
          icon={<BellRing size={20} aria-hidden="true" />}
          title="No open follow-ups - your queue is clear."
          testId="coc-cockpit-empty"
        />
      ) : (
        <ul className="coc-cockpit__list" data-testid="coc-cockpit-list">
          {state.rows.map((row) => (
            <CocFollowupRow
              key={String(row.id)}
              row={row}
              nowMs={nowMs}
              busy={busyId === row.id}
              onAction={onAction}
            />
          ))}
        </ul>
      )}

      <p
        className="clinic-workbench__note coc-cockpit__ceiling"
        role="note"
        data-testid="coc-cockpit-ceiling"
      >
        {COC_COCKPIT_CEILING_NOTE}
      </p>
    </section>
  );
}

// --- Stateful wirer (default export) ---------------------------------------

export default function CocFollowupCockpit(): JSX.Element {
  const [state, setState] = useState<CockpitState>({ kind: 'loading' });
  const [busyId, setBusyId] = useState<number | null>(null);
  // Captured once at mount and re-stamped on each (re)load so the age labels are
  // stable within a load rather than ticking; the pure helper takes it injected.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  const load = useCallback(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    setNowMs(Date.now());
    fetchMyWorkActions()
      .then((rows) => {
        if (!cancelled) setState({ kind: 'ready', rows });
      })
      .catch(() => {
        // Honest "unavailable" state rather than a fabricated empty/zero.
        if (!cancelled) setState(COCKPIT_ERROR_STATE);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  const onAction = useCallback(
    (id: number, status: FollowupTransition) => {
      setBusyId(id);
      performFollowupTransition(transitionWorkAction, id, status)
        .then((ok) => {
          // On success the action left the open queue - drop the row (reusing
          // removeFollowupRow via applyTransitionResult); on failure the row
          // stays. Never fabricate a success.
          setState((prev) => applyTransitionResult(prev, id, ok));
        })
        .finally(() => {
          setBusyId((b) => (b === id ? null : b));
        });
    },
    [],
  );

  return (
    <CocCockpitView
      state={state}
      nowMs={nowMs}
      busyId={busyId}
      onAction={onAction}
    />
  );
}
